import { FC } from 'react'
import {
	Badge,
	Box,
	Button,
	ColumnLayout,
	Container,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'

import {
	describeSandboxSchedule,
	resolveSandboxScheduleCronSyntax
} from '../../../../helper/gcpProjectSandboxSchedule'
import { usegcpProjectCreationFormStore } from '../../../../stores/gcpProjects/newgcpProjectCreationFormStore'
import { useGenericTagsManagementFormStore } from '../../../../stores/tags/genericTagsManagementFormStore'
import { keysToLabel } from '../../../../helper/toCamelCase'

const getSelectedProjectType = (gcpProjectType: unknown): string => {
	if (typeof gcpProjectType === 'string') {
		return gcpProjectType
	}

	if (
		gcpProjectType &&
		typeof gcpProjectType === 'object' &&
		'value' in gcpProjectType
	) {
		return String((gcpProjectType as { value?: string }).value ?? '')
	}

	return ''
}

const renderValue = (value: unknown): string => {
	if (Array.isArray(value)) {
		const entries = value
			.map((item) => renderValue(item))
			.filter((item) => item !== 'NA')

		return entries.length > 0 ? entries.join(', ') : 'NA'
	}

	if (typeof value === 'object' && value !== null) {
		return (value as { value?: string }).value ?? 'NA'
	}

	if (value === '' || value === undefined || value === null) {
		return 'NA'
	}

	return String(value)
}

const Review: FC = () => {
	const {
		formValues,
		actions: { setCurrentStep }
	} = usegcpProjectCreationFormStore()

	const { formValues: tagformValues } = useGenericTagsManagementFormStore()

	const excludedKeys = ['Tag Project', 'Data Classification']
	const tagKeys = Object.keys(tagformValues).filter(
		(key) => !excludedKeys.includes(keysToLabel(key))
	)

	const gcpProjectType = getSelectedProjectType(formValues.gcpProjectType)
	const infrastructureType = formValues.infrastructureType || 'GKE'
	const sandboxSchedule = {
		mode: formValues[`${gcpProjectType}ScheduleMode`],
		frequency: formValues[`${gcpProjectType}CronFrequency`],
		day: formValues[`${gcpProjectType}CronDayOfWeek`],
		dayOfMonth: formValues[`${gcpProjectType}CronDayOfMonth`],
		month: formValues[`${gcpProjectType}CronMonth`],
		time: formValues[`${gcpProjectType}CronTime`],
		cronSyntax: formValues[`${gcpProjectType}CronSyntax`]
	}

	return (
		<SpaceBetween direction="vertical" size="l">
			<Header
				variant="h3"
				actions={<Button onClick={() => setCurrentStep(0)}>Edit</Button>}
			>
				Step 1: Project Types
			</Header>
			<Container>
				<ColumnLayout columns={2} variant="text-grid">
					<div>
						<Box variant="awsui-key-label">GCP Project Type</Box>
						<Badge color="blue">{gcpProjectType || 'N/A'}</Badge>
					</div>
				</ColumnLayout>
			</Container>

			<Header
				variant="h3"
				actions={<Button onClick={() => setCurrentStep(1)}>Edit</Button>}
			>
				Step 2: Project Details
			</Header>
			<Container header={<Header variant="h3">Project Details</Header>}>
				<ColumnLayout columns={2} variant="text-grid">
					{gcpProjectType !== 'Sandbox' && (
						<div>
							<Box variant="awsui-key-label">Infrastructure Type</Box>
							{infrastructureType}
						</div>
					)}
					<div>
						<Box variant="awsui-key-label">
							Project Viewer Distribution List
						</Box>
						{renderValue(
							formValues[`${gcpProjectType}ProjectViewerDistributionList`]
						)}
					</div>
					<div>
						<Box variant="awsui-key-label">Budget Alerts Distribution List</Box>
						{renderValue(
							formValues[`${gcpProjectType}BudgetAlertsDistributionList`]
						)}
					</div>
					<div>
						<Box variant="awsui-key-label">Platform Owner Email</Box>
						{renderValue(formValues[`${gcpProjectType}PlatformOwnerEmail`])}
					</div>
					<div>
						<Box variant="awsui-key-label">Project Monthly Budget Cap</Box>
						{renderValue(formValues[`${gcpProjectType}MonthlyBudgetCap`])}
					</div>
				</ColumnLayout>
			</Container>
		{gcpProjectType === 'Sandbox' && (
			<Container
				header={<Header variant="h3">Resource Nuker Pipeline Details</Header>}
			>
				<ColumnLayout columns={2} variant="text-grid">
						<>
							<div>
								<Box variant="awsui-key-label">Cron Timezone</Box>
								{renderValue(formValues[`${gcpProjectType}CronTimezone`])}
							</div>
							<div>
								<Box variant="awsui-key-label">Schedule</Box>
								{describeSandboxSchedule(sandboxSchedule)}
							</div>
							<div>
								<Box variant="awsui-key-label">Cron Syntax</Box>
								{resolveSandboxScheduleCronSyntax(sandboxSchedule)}
							</div>
						</>
				</ColumnLayout>
			</Container>
		)}
			<Header
				variant="h3"
				actions={<Button onClick={() => setCurrentStep(2)}>Edit</Button>}
			>
				Step 3: Tag Details
			</Header>
			<Container>
				<ColumnLayout columns={2} variant="text-grid">
					{tagKeys.map((tagkey) => (
						<div key={tagkey}>
							<Box variant="awsui-key-label">
								{keysToLabel(tagkey) === 'W B S Code'
									? 'WBS Code'
									: keysToLabel(tagkey)}
							</Box>
							{renderValue(tagformValues[tagkey])}
						</div>
					))}
				</ColumnLayout>
			</Container>
		</SpaceBetween>
	)
}

export default Review
